/**
 * TableOfContents Component
 * Extracts headings from markdown content and displays a sticky TOC
 */
import React, { useState, useEffect } from 'react';
import { List } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

// Extract headings from markdown content
const extractHeadings = (content) => {
  const headingRegex = /^#{2,3}\s+(.+)$/gm;
  const headings = [];
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[0].startsWith('###') ? 3 : 2;
    const text = match[1].trim();
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9áéíóúüñ\s]/g, '')
      .replace(/\s+/g, '-');

    headings.push({ id, text, level });
  }

  return headings;
};

const TableOfContents = ({ content, className }) => {
  const { t } = useTranslation();
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (content) {
      setHeadings(extractHeadings(content));
    }
  }, [content]);

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -66%' }
    );

    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) {
    return null;
  }

  const handleClick = (e, id) => {
    e.preventDefault();
    setActiveId(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={cn(
        'rounded-2xl p-5',
        'border border-neutral-200 bg-white',
        'dark:border-neutral-800 dark:bg-neutral-900',
        'flex max-h-[calc(100vh-8rem)] flex-col',
        className
      )}
    >
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
        <List className="h-4 w-4" strokeWidth={2.5} />
        <span>{t('blog.tableOfContents', 'Contenido')}</span>
      </div>

      <ul className="custom-scrollbar flex-1 space-y-2 overflow-y-auto pr-2">
        {headings.map((heading) => (
          <li key={heading.id} className={cn(heading.level === 3 && 'ml-4')}>
            <a
              href={`#${heading.id}`}
              onClick={(e) => handleClick(e, heading.id)}
              className={cn(
                'block text-sm transition-colors duration-200',
                'hover:text-primary-600 dark:hover:text-primary-400',
                activeId === heading.id
                  ? 'font-medium text-primary-600 dark:text-primary-400'
                  : 'text-neutral-600 dark:text-neutral-400'
              )}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export { extractHeadings };
export default TableOfContents;
